import React, { useState, useEffect } from "react";
import { getBusinesses, sendContactEmail } from "../utils/api";
import BusinessCard from "../components/BusinessCard";
import {
  Search,
  Sparkles,
  ArrowRight,
  MessageSquare,
  CalendarCheck,
  BarChart3,
  CheckCircle2,
  Phone,
  Mail,
  MapPin
} from "lucide-react";
import "./Home.css";

export default function Home() {
  const [businesses, setBusinesses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const [contactForm, setContactForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [contactStatus, setContactStatus] = useState({ loading: false, message: "", type: "" });

  // When search or category changes, reset page to 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    async function fetchBusinesses() {
      try {
        setLoading(true);
        const data = await getBusinesses();
        setBusinesses(data);
      } catch (err) {
        setError("İşletmeler yüklenirken bir hata oluştu.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchBusinesses();
  }, []);

  const categories = ["ALL", ...new Set(businesses.map((b) => b.category))];

  const filtered = businesses.filter((b) => {
    const matchesSearch =
      b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "ALL" || b.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedBusinesses = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setContactStatus({ loading: false, message: "Lütfen ad, e-posta ve mesaj alanlarını doldurun.", type: "error" });
      return;
    }
    try {
      setContactStatus({ loading: true, message: "", type: "" });
      const res = await sendContactEmail(contactForm);
      setContactStatus({ loading: false, message: res.message || "Mesajınız gönderildi.", type: "success" });
      setContactForm({ name: "", phone: "", email: "", message: "" });
      setTimeout(() => setContactStatus({ loading: false, message: "", type: "" }), 5000);
    } catch (err) {
      setContactStatus({ loading: false, message: err.message || "Bir hata oluştu.", type: "error" });
    }
  };

  return (
    <div className="home-landing">
      {/* ================= HERO SECTION ================= */}
      <section className="home-hero">
        <div className="home-container" style={{ display: 'flex', alignItems: 'center', width: '100%', flexDirection: 'inherit' }}>

          <div className="hero-content-wrapper">
            <div className="hero-badge">
              <Sparkles />
              Yeni Nesil Randevu Sistemi
            </div>

            <h1>
              Aradığınız Hizmeti
              <span>Hemen Bulun</span>
            </h1>

            <p>
              İstediğiniz işletmeden, istediğiniz zaman online randevu alın.
              Sıra bekleme devri bitti. Profesyonel hizmetler sadece bir tık uzağınızda.
            </p>

            <div className="hero-actions">
              <a href="#marketplace" className="hero-btn-primary">
                İşletmeleri Keşfet <ArrowRight size={20} />
              </a>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-mockup">
              <div className="mockup-header">
                <div className="mockup-dot" style={{ background: '#ff5f56' }} />
                <div className="mockup-dot" style={{ background: '#ffbd2e' }} />
                <div className="mockup-dot" style={{ background: '#27c93f' }} />
              </div>
              <div className="mockup-body">
                <div className="mockup-card" />
                <div className="mockup-card" />
                <div className="mockup-card" />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ================= MARKETPLACE SECTION ================= */}
      <section id="marketplace" className="home-marketplace">
        <div className="home-container">

          <div className="section-header">
            <h2>İşletmeleri Keşfet</h2>
            <p>İhtiyacınıza en uygun hizmeti sağlayan seçkin işletmeleri inceleyin, değerlendirmeleri okuyun ve anında randevunuzu oluşturun.</p>
          </div>

          <div className="marketplace-controls">
            <div className="mp-search">
              <Search />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="İşletme adı veya hizmet ara..."
              />
            </div>

            <div className="mp-categories">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`mp-cat-btn ${selectedCategory === cat ? "active" : ""}`}
                >
                  {cat === "ALL" ? "Tümü" : cat}
                </button>
              ))}
            </div>
          </div>

          {/* Loading / Error States */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "4rem 0" }}>
              <p style={{ color: "#888", fontSize: "1.1rem" }}>İşletmeler yükleniyor...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "4rem 0" }}>
              <p style={{ color: "#ef4444", fontSize: "1.1rem" }}>{error}</p>
            </div>
          ) : (
            <>
              {paginatedBusinesses.length > 0 ? (
                <>
                  <div className="business-grid">
                    {paginatedBusinesses.map((business) => (
                      <BusinessCard key={business.id} business={business} />
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="home-pagination">
                      <button
                        className="home-pagination-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Önceki
                      </button>
                      <span className="home-pagination-info">
                        Sayfa {currentPage} / {totalPages}
                      </span>
                      <button
                        className="home-pagination-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Sonraki
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "4rem 0", color: "#666" }}>
                  Aramanıza uygun işletme bulunamadı. Lütfen farklı bir terim deneyin.
                </div>
              )}
            </>
          )}

        </div>
      </section>

      {/* ================= FEATURES (SaaS INTRO) SECTION ================= */}
      <section className="home-features">
        <div className="home-container">

          <div className="section-header">
            <h2>İşletmeler İçin Rezervioo</h2>
            <p>İşletmenizi dijitale taşıyın, müşteri memnuniyetini artırın ve gelirinizi katlayın.</p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <MessageSquare />
              </div>
              <h3>SMS Doğrulama</h3>
              <p>Müşterilerinizin randevularını SMS ile doğrulayarak sahte rezervasyonların önüne geçin ve gelmeyen müşteri oranını düşürün.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <CalendarCheck />
              </div>
              <h3>Kolay Yönetim</h3>
              <p>Personel takvimi, çalışma saatleri ve tatil günlerini tek bir ekrandan zahmetsizce yöneterek verimliliği artırın.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <BarChart3 />
              </div>
              <h3>Gelişmiş İstatistikler</h3>
              <p>İşletmenizin performansını analiz edin. Hangi hizmetlerin ve personelin daha çok tercih edildiğini detaylı raporlarla görün.</p>
            </div>
          </div>

        </div>
      </section>

      {/* ================= PRICING SECTION ================= */}
      <section className="home-pricing">
        <div className="home-container">

          <div className="section-header">
            <h2>Fiyatlandırma</h2>
            <p>İhtiyaçlarınıza en uygun paketi seçin, hiçbir gizli ücret ödemeden işletmenizi büyütmeye başlayın.</p>
          </div>

          <div className="pricing-grid">

            <div className="pricing-card">
              <h3>Eco</h3>
              <div className="price">₺1.299<span>/ay</span></div>
              <p className="pricing-desc">Küçük işletmeler ve tek çalışanlı salonlar için ideal.</p>
              <ul className="pricing-features">
                <li><CheckCircle2 /> 1 Personel Hesabı</li>
                <li><CheckCircle2 /> Aylık 100 Randevu Limiti</li>
                <li><CheckCircle2 /> Randevu Takibi</li>
                <li><CheckCircle2 /> SMS Doğrulama Sistemi</li>
              </ul>
              <button 
                className="pricing-btn"
                onClick={() => {
                  setContactForm({...contactForm, message: "Eco paket hakkında bilgi almak istiyorum."})
                  window.location.href = "#contactsection"
                }}
              >İletişime Geç</button>
            </div>

            <div className="pricing-card popular">
              <div className="popular-badge">En Popüler</div>
              <h3>Standard</h3>
              <div className="price">₺1.899<span>/ay</span></div>
              <p className="pricing-desc">Büyümekte olan ve ekibi olan işletmeler için mükemmel.</p>
              <ul className="pricing-features">
                <li><CheckCircle2 /> Eco paketi özellikleri</li>
                <li><CheckCircle2 /> 5 Personel Hesabı</li>
                <li><CheckCircle2 /> Sınırsız Randevu</li>
                <li><CheckCircle2 /> SMS İle Randevu Hatırlatma</li>
                <li><CheckCircle2 /> SMS İle İşletme Memnuniyeti Puanlama</li>
              </ul>
              <button 
                className="pricing-btn"
                onClick={() => {
                  setContactForm({...contactForm, message: "Standard paket hakkında bilgi almak istiyorum."})
                  window.location.href = "#contactsection"
                }}
              >İletişime Geç</button>
            </div>

            <div className="pricing-card">
              <h3>Pro</h3>
              <div className="price">₺2.499<span>/ay</span></div>
              <p className="pricing-desc">Geniş ekipli veya çok şubeli markalar için tasarlandı.</p>
              <ul className="pricing-features">
                <li><CheckCircle2 /> Standard paketi özellikleri</li>
                <li><CheckCircle2 /> Sınırsız Personel</li>
                <li><CheckCircle2 /> İşletme Analiz Raporları</li>
                <li><CheckCircle2 /> Personele Özel Raporlar</li>
              </ul>
              <button 
                className="pricing-btn"
                onClick={() => {
                  setContactForm({...contactForm, message: "Pro paket hakkında bilgi almak istiyorum."})
                  window.location.href = "#contactsection"
                }}
              >İletişime Geç</button>
            </div>

          </div>
        </div>
      </section>

      {/* ================= CONTACT SECTION ================= */}
      <section id="contactsection" className="home-contact">
        <div className="home-container">
          <div className="contact-wrapper">

            <div className="contact-info">
              <h2>Bizimle İletişime Geçin</h2>
              <p>Sorularınız mı var veya özel bir entegrasyon mu istiyorsunuz? Ekibimiz size yardımcı olmaktan memnuniyet duyar.</p>

              <div className="contact-details">
                {/*<div className="contact-item">
                  <Phone />
                  <span>+90 850 123 45 67</span>
                </div>*/}
                <div className="contact-item">
                  <Mail />
                  <span>info@rezervioo.com</span>
                </div>
                <div className="contact-item">
                  <MapPin />
                  <span>Palandöken, Erzurum, Türkiye</span>
                </div>
              </div>
            </div>

            <div className="contact-form">
              <form onSubmit={handleContactSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Ad Soyad</label>
                    <input type="text" placeholder="John Doe" value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Telefon</label>
                    <input type="tel" placeholder="05xx xxx xx xx" value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <label>E-posta</label>
                  <input type="email" placeholder="ornek@sirket.com" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Mesajınız</label>
                  <textarea rows="4" placeholder="Size nasıl yardımcı olabiliriz?" style={{ resize: 'vertical' }} value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})} required></textarea>
                </div>
                {contactStatus.message && (
                  <div style={{ marginBottom: '1rem', color: contactStatus.type === 'error' ? '#ef4444' : '#22c55e', fontWeight: 500 }}>
                    {contactStatus.message}
                  </div>
                )}
                <button type="submit" className="submit-btn" disabled={contactStatus.loading}>
                  {contactStatus.loading ? "Gönderiliyor..." : "Gönder"}
                </button>
              </form>
            </div>

          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="home-footer">
        <div className="home-container">
          <div className="footer-content">
            <div className="footer-logo">
              <Sparkles size={24} color="#d4af37" />
              Rezervioo
            </div>

            <div className="footer-links">
              <a href="#">Hakkımızda</a>
              <a href="#">Kullanım Koşulları</a>
              <a href="#">Gizlilik Politikası</a>
              <a href="#">KVKK Aydınlatma Metni</a>
              <a href="#">S.S.S.</a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Rezervioo. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
